import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type VideoHTMLAttributes,
} from 'react';

// If the video has not buffered enough to play through within this window,
// unlock the CTA so a slow/offline network never traps the user on the intro.
const LOAD_TIMEOUT_MS = 7000;
const PROGRESS_KEY_PREFIX = 'belong_intro_video_progress:';

function formatTime(seconds: number): string {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

interface UseIntroVideoOptions {
  /** Stable identifier so resume-on-refresh is scoped per video/flow. */
  storageKey: string;
  /** Gate already satisfied (completed step, dev bypass). Skips spinner + gating. */
  initiallyUnlocked?: boolean;
}

export interface UseIntroVideoResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Show a spinner over the video frame until it can play through. */
  isLoading: boolean;
  /** CTA should be enabled. */
  isUnlocked: boolean;
  /** 0-100 playback progress. */
  progress: number;
  /** Formatted MM:SS remaining. */
  remainingTime: string;
  /** Whether a countdown should be shown (duration known and still gated). */
  showCountdown: boolean;
  /** Toggle mute without pausing (the frame is click-to-mute). */
  toggleMute: () => void;
  /** Spread onto the <video> element. */
  videoProps: Pick<
    VideoHTMLAttributes<HTMLVideoElement>,
    'onTimeUpdate' | 'onEnded' | 'onError' | 'onPause'
  >;
}

export function useIntroVideo({
  storageKey,
  initiallyUnlocked = false,
}: UseIntroVideoOptions): UseIntroVideoResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const clickPauseRef = useRef(false);
  const fullStorageKey = `${PROGRESS_KEY_PREFIX}${storageKey}`;

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoJustWatched, setVideoJustWatched] = useState(false);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  // Ready = buffered enough to play through; controls the spinner.
  const [isReady, setIsReady] = useState(initiallyUnlocked);
  // Resumed from a saved position (a prior, accidental refresh) -> unlock CTA.
  const [resumed, setResumed] = useState(false);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    try {
      window.localStorage.setItem(fullStorageKey, String(video.currentTime));
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [fullStorageKey]);

  const handleEnded = useCallback(() => {
    setVideoJustWatched(true);
    try {
      window.localStorage.removeItem(fullStorageKey);
    } catch {
      // ignore
    }
  }, [fullStorageKey]);

  const handleError = useCallback(() => {
    // Never trap the user behind a broken video.
    setVideoJustWatched(true);
    setIsReady(true);
  }, []);

  const handlePause = useCallback(() => {
    const video = videoRef.current;
    if (clickPauseRef.current && video && !video.ended) {
      clickPauseRef.current = false;
      video.play().catch(() => {});
    }
  }, []);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    clickPauseRef.current = true;
    video.muted = !video.muted;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let savedTime = 0;
    try {
      savedTime = parseFloat(window.localStorage.getItem(fullStorageKey) ?? '');
    } catch {
      savedTime = 0;
    }
    if (!Number.isFinite(savedTime)) savedTime = 0;

    const restorePosition = () => {
      setDuration(video.duration);
      if (savedTime > 0 && savedTime < video.duration - 0.5) {
        try {
          video.currentTime = savedTime;
        } catch {
          // ignore seek failures
        }
        setCurrentTime(savedTime);
        // They've seen part of it before -> don't force a re-watch.
        setResumed(true);
      }
    };

    // Fallback: slow network -> unlock the CTA after the timeout even though
    // the video keeps loading behind the spinner. Declared before the
    // synchronous readyState checks so startPlayback can clear it without
    // hitting the temporal dead zone on an already-cached video.
    const timeoutId = window.setTimeout(() => {
      setLoadTimedOut(true);
    }, LOAD_TIMEOUT_MS);

    const startPlayback = () => {
      window.clearTimeout(timeoutId);
      video.play().catch(() => {});
    };

    // Hide the spinner once the video is actually playing, regardless of how
    // playback was unlocked (canplaythrough or the slow-network timeout).
    const onPlaying = () => {
      setIsReady(true);
    };

    if (video.readyState >= 1) restorePosition();
    if (video.readyState >= 4) startPlayback();

    video.addEventListener('loadedmetadata', restorePosition);
    video.addEventListener('canplaythrough', startPlayback);
    video.addEventListener('playing', onPlaying);

    return () => {
      window.clearTimeout(timeoutId);
      video.removeEventListener('loadedmetadata', restorePosition);
      video.removeEventListener('canplaythrough', startPlayback);
      video.removeEventListener('playing', onPlaying);
    };
  }, [fullStorageKey]);

  const isUnlocked =
    initiallyUnlocked || videoJustWatched || loadTimedOut || resumed;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const remainingTime = duration > 0 ? formatTime(duration - currentTime) : '';
  const showCountdown = duration > 0 && !isUnlocked;

  return {
    videoRef,
    isLoading: !isReady,
    isUnlocked,
    progress: isUnlocked ? 100 : progress,
    remainingTime,
    showCountdown,
    toggleMute,
    videoProps: {
      onTimeUpdate: handleTimeUpdate,
      onEnded: handleEnded,
      onError: handleError,
      onPause: handlePause,
    },
  };
}
